<template>
  <!-- OneSignal handles native permission UI, only show errors -->
  <div
    v-if="registrationError"
    class="fixed bottom-4 left-4 max-w-sm bg-red-50 border border-red-200 rounded-lg p-4 z-50"
  >
    <div class="flex">
      <div class="flex-shrink-0">
        <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          />
        </svg>
      </div>
      <div class="ml-3">
        <h3 class="text-sm font-medium text-red-800">Notification Setup Failed</h3>
        <p class="text-sm text-red-700 mt-1">{{ registrationError }}</p>
        <button
          @click="registerDeviceToken"
          class="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch, onMounted } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { useOneSignalRegistration } from '@/composables/useOneSignalRegistration'
import { useRoute } from 'vue-router'

const authStore = useAuthStore()
const route = useRoute()
const { registerDeviceToken, registrationError } = useOneSignalRegistration()

// Simple check: should we register for this user?
const shouldRegister = () => {
  // Skip registration in development mode
  /* if (import.meta.env.DEV) {
    return false
  } */

  const currentUserId = authStore.user?.id?.toString()
  const lastRegisteredUserId = sessionStorage.getItem('oneSignalRegisteredUserId')

  // Don't register if already done for this user
  if (currentUserId && currentUserId === lastRegisteredUserId) {
    return false
  }

  return true
}

// Check if we're on a page where we should show notification popup
const shouldShowNotificationPopup = () => {
  const currentPath = route.path

  // Don't show popup on login/verification pages
  const authPages = ['/login', '/verify-code']

  return !authPages.includes(currentPath)
}

// Debounced registration function to prevent multiple calls
let registrationTimeout: ReturnType<typeof setTimeout> | null = null

const attemptRegistration = () => {
  if (registrationTimeout) {
    clearTimeout(registrationTimeout)
  }
  
  registrationTimeout = setTimeout(() => {
    if (authStore.isLoggedIn && shouldRegister() && shouldShowNotificationPopup()) {
      console.log('🔔 Starting OneSignal registration')
      registerDeviceToken()
    }
  }, 2000) // Single delay to let page settle
}

// When user logs in, trigger OneSignal registration
watch(
  () => authStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn) {
      console.log('🔔 User logged in, scheduling registration check...')
      attemptRegistration()
    }
  },
)

// Watch for route changes to trigger notification popup after navigation
watch(
  () => route.path,
  (newPath) => {
    if (authStore.isLoggedIn) {
      console.log('🔔 Route changed to:', newPath, '- scheduling registration check')
      attemptRegistration()
    }
  },
)

// Check on mount if user is already logged in
onMounted(() => {
  if (authStore.isLoggedIn) {
    console.log('🔔 Component mounted with logged in user, scheduling registration check...')
    attemptRegistration()
  }
})
</script>
